import { useEffect, useState } from "react";
import type { ClientDepositStatusId } from "@tg/shared";

// Подписи/иконки статусов клиентов по депозитам (запрос №22). Сама логика
// (пороги, вычисление) — в @tg/shared/client-status, общая с gateway.

export interface ClientStatusMeta {
  id: ClientDepositStatusId;
  emoji: string;
  label: string;
  /** Множественное число для чипа фильтра («Горячие»). */
  plural: string;
  /** Как описывать окно в настройках. */
  hint: string;
}

export const CLIENT_STATUS_META: Record<ClientDepositStatusId, ClientStatusMeta> = {
  hot: { id: "hot", emoji: "🔥", label: "Горячий", plural: "Горячие", hint: "сразу после депозита" },
  warm: { id: "warm", emoji: "☀️", label: "Тёплый", plural: "Тёплые", hint: "недавний депозит" },
  cold: { id: "cold", emoji: "❄️", label: "Остывший", plural: "Остывшие", hint: "депозит на прошлой неделе" },
  sleeping: { id: "sleeping", emoji: "🌙", label: "Спящий", plural: "Спящие", hint: "давно без депозита" },
  marinade: { id: "marinade", emoji: "📦", label: "Маринад", plural: "Маринад", hint: "очень давно без депозита" }
};

/** «12 мин», «3 ч», «5 дн», «4 мес» — давность последнего депозита. */
export const formatDepositAge = (lastDepositAt: string, now: number): string => {
  const at = Date.parse(lastDepositAt);
  if (!Number.isFinite(at)) {
    return "";
  }
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  if (minutes < 60) {
    return `${minutes} мин`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 48) {
    return `${hours} ч`;
  }
  const days = Math.floor(hours / 24);
  if (days < 60) {
    return `${days} дн`;
  }
  return `${Math.floor(days / 30)} мес`;
};

/** Часы → человекочитаемо для настроек: 24 → «1 день», 36 → «36 ч». */
export const formatHoursWindow = (hours: number): string => {
  if (hours % 24 === 0) {
    const days = hours / 24;
    const mod10 = days % 10;
    const mod100 = days % 100;
    const word =
      mod10 === 1 && mod100 !== 11
        ? "день"
        : mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)
          ? "дня"
          : "дней";
    return `${days} ${word}`;
  }
  return `${hours} ч`;
};

/** Текущее время, обновляемое раз в минуту — статусы «стареют» сами. */
export const useMinuteTick = (): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
};
